import React, { useState, useEffect } from 'react';
import {
  Accordion, Table, Badge, Form, Row, Col, Button, Card, Alert, Modal, ProgressBar
} from 'react-bootstrap';
import { FaLock, FaShieldAlt, FaKey, FaCheck, FaTimes, FaPlus, FaEdit } from 'react-icons/fa';
import axios from 'axios';

const CCPASecurityEncryptionTab = ({ userData, onScoreUpdate, showNotification, region , refreshActivities,logToAccessLogs}) => {
  const [securityMeasures, setSecurityMeasures] = useState([]);
  const [encryptionStatus, setEncryptionStatus] = useState({});
  const [accessControls, setAccessControls] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newMeasure, setNewMeasure] = useState({
    category: '',
    measure: '',
    implementation: 'Implemented',
    description: '',
    responsibleParty: '',
    reviewDate: '',
    ccpaRelevance: ''
  });
const [showAccessControlModal, setShowAccessControlModal] = useState(false);
const [newAccessControl, setNewAccessControl] = useState({
  type: '',
  description: '',
  status: 'Active',
  coverage: 0
});


  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  const SECURITY_CATEGORIES = [
    'Technical Safeguards',
    'Administrative Safeguards',
    'Physical Safeguards',
    'Access Controls',
    'Data Encryption',
    'Network Security',
    'Incident Response',
    'Employee Training',
    'Vendor Security',
    'Data Breach Prevention'
  ];

  const CCPA_RELEVANCE = [
    'Consumer Request Processing',
    'Personal Information Protection',
    'Sensitive Personal Information',
    'Data Sale Prevention',
    'Third-Party Data Sharing',
    'Identity Verification',
    'Data Breach Response'
  ];

  const ACCESS_CONTROL_TYPES = [
  'Identity Verification',
  'Data Minimization',
  'Role-Based Access Control',
  'Multi-Factor Authentication',
  'Data Loss Prevention',
  'Audit Logging',
  'Session Management',
  'Encryption Key Management',
  'Consumer Request Access',
  'Data Retention Controls'
];


  useEffect(() => {
    if (userData?.['cognito:username']) {
    fetchSecurityData();
  }
}, [userData]);

  const fetchSecurityData = async () => {
    try {
      const [measuresRes, encryptionRes, accessRes] = await Promise.all([
        axios.get('https://5zr32hdoe2.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/security-measures/list', { headers }),
        axios.get('https://0rzogrkm02.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/encryption-status', { headers }),
        axios.get('https://yaop2kzfq4.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/access-controls/list', { headers })
      ]);

      setSecurityMeasures(measuresRes.data);
      setEncryptionStatus(encryptionRes.data);
      setAccessControls(accessRes.data);
      
      calculateSecurityScore(measuresRes.data, encryptionRes.data, accessRes.data);
    } catch (error) {
      console.error('Error fetching security data:', error);
    }
  };

  const calculateSecurityScore = (measures, encryption, access) => {
    let score = 0;
    
    // CCPA Security Requirements (reasonable security procedures)
    const implementedMeasures = measures.filter(m => m.implementation === 'Implemented').length;
    const totalMeasures = measures.length || 1;
    const technicalScore = (implementedMeasures / totalMeasures) * 35;
    
    // Encryption Coverage (CCPA doesn't mandate but strongly recommended)
    const encryptionCoverage = encryption.dataAtRestEncrypted && encryption.dataInTransitEncrypted ? 30 : 
                              (encryption.dataAtRestEncrypted || encryption.dataInTransitEncrypted) ? 15 : 0;
    
    // Access Controls for Consumer Data
    const hasIdentityVerification = access.some(a => a.type === 'Identity Verification');
    const hasDataMinimization = access.some(a => a.type === 'Data Minimization');
    const accessScore = (hasIdentityVerification ? 17.5 : 0) + (hasDataMinimization ? 17.5 : 0);
    
    score = Math.round(technicalScore + encryptionCoverage + accessScore);
     if (onScoreUpdate) {
    onScoreUpdate(Math.min(100, Math.max(0, score)));
     }
  };

  const handleSaveMeasure = async () => {
    try {
      await axios.post('https://60v2v7tsf4.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/security-measures/create', newMeasure, { headers });
      await logActivity(`Added CCPA security measure: ${newMeasure.measure}`, "success");
      logToAccessLogs?.({
        action:      'Security Entry Created',
        severity:    'success',
        description: `Added CCPA security measure: ${newMeasure.measure} (${newMeasure.category})`,});

      showNotification('Security measure added successfully!');
      fetchSecurityData();
      resetForm();
    } catch (error) {
      console.error('Error saving security measure:', error);
      showNotification('Error saving security measure', 'danger');
    }
  };

  const handleSaveAccessControl = async () => {
  try {
    await axios.post('https://atgnnsz0yi.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/access-controls/create', newAccessControl, { headers });
    await logActivity(`Added CCPA access control: ${newAccessControl.type}`, "success");
    logToAccessLogs?.({
        action:      'Access Control Added',
        severity:    'success',
        description: `Added CCPA access control: ${newAccessControl.type}`,});
    showNotification('Access control added successfully!');
    fetchSecurityData();
    resetAccessControlForm();
  } catch (error) {
    console.error('Error saving access control:', error);
    showNotification('Error saving access control', 'danger');
  }
};


  const resetForm = () => {
    setNewMeasure({
      category: '',
      measure: '',
      implementation: 'Implemented',
      description: '',
      responsibleParty: '',
      reviewDate: '',
      ccpaRelevance: ''
    });
    setShowModal(false);
  };

  const resetAccessControlForm = () => {
  setNewAccessControl({
    type: '',
    description: '',
    status: 'Active',
    coverage: 0
  });
  setShowAccessControlModal(false);
};


 const logActivity = async (message, badge = "info") => {
 
  
  try {
    // ✅ Map frontend fields to backend entity fields
    const payload = {
      action: badge,                    // badge → action
      module: "Security/Encryption",              // Add module field
      description: message                  // message → details
      // userId and timestamp set by backend
    };
    
    
    const response = await axios.post(
      "https://wjioewyrif.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/activities/create", 
      payload,     
      { headers }  
    );
    
    
    if (refreshActivities) {
        await refreshActivities();
      }


  } catch (err) {
    console.error('❌ Error logging inventory activity:', err);
    
    if (err.response) {
      console.error('🔥 Server Error Details:');
      console.error('Status:', err.response.status);
      console.error('Data:', err.response.data);
    }
    
    console.warn("Failed to log activity:", err.message);
  }
};

 const [activeAccordionKey, setActiveAccordionKey] = useState(null);

  return (
    <>
       <div className="border rounded">
  <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "0" ? null : "0")
    }
  >
    <h6>Security Measures Overview</h6>
    <span>{activeAccordionKey === "0" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "0" && (
    <div className="p-3 border-top">
            <Alert variant="info">
              <strong>CCPA Security Requirement:</strong> Businesses must implement reasonable security procedures 
              and practices to protect personal information from unauthorized access, destruction, use, or disclosure[19][27].
            </Alert>

            <Row className="mb-3">
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-primary">{securityMeasures.filter(m => m.implementation === 'Implemented').length}</h4>
                    <small>Implemented</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-warning">{securityMeasures.filter(m => m.implementation === 'In Progress').length}</h4>
                    <small>In Progress</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-danger">{securityMeasures.filter(m => m.implementation === 'Not Implemented').length}</h4>
                    <small>Not Implemented</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4>{securityMeasures.length}</h4>
                    <small>Total Measures</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Button variant="primary" className="mb-3" onClick={() => setShowModal(true)}>
              <FaPlus /> Add Security Measure
            </Button>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Security Measure</th>
                  <th>Implementation Status</th>
                  <th>CCPA Relevance</th>
                  <th>Responsible Party</th>
                  <th>Review Date</th>
                </tr>
              </thead>
              <tbody>
                {securityMeasures.map((measure, index) => (
                  <tr key={index}>
                    <td>{measure.category}</td>
                    <td>{measure.measure}</td>
                    <td>
                      <Badge bg={
                        measure.implementation === 'Implemented' ? 'success' :
                        measure.implementation === 'In Progress' ? 'warning' : 'danger'
                      }>
                        {measure.implementation}
                      </Badge>
                    </td>
                    <td>{measure.ccpaRelevance}</td>
                    <td>{measure.responsibleParty}</td>
                    <td>{measure.reviewDate}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          )}


          
         

        <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "1" ? null : "1")
    }
  >
    <h6>Encryption Status</h6>
    <span>{activeAccordionKey === "1" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "1" && (
    <div className="p-3 border-top">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title>Personal Information At Rest</Card.Title>
                    <div className="d-flex align-items-center">
                      {encryptionStatus.dataAtRestEncrypted ? 
                        <FaCheck className="text-success me-2" /> : 
                        <FaTimes className="text-danger me-2" />
                      }
                      <span>{encryptionStatus.dataAtRestEncrypted ? 'Encrypted' : 'Not Encrypted'}</span>
                    </div>
                    <small className="text-muted">Algorithm: {encryptionStatus.atRestAlgorithm || 'Not specified'}</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title>Personal Information In Transit</Card.Title>
                    <div className="d-flex align-items-center">
                      {encryptionStatus.dataInTransitEncrypted ? 
                        <FaCheck className="text-success me-2" /> : 
                        <FaTimes className="text-danger me-2" />
                      }
                      <span>{encryptionStatus.dataInTransitEncrypted ? 'Encrypted' : 'Not Encrypted'}</span>
                    </div>
                    <small className="text-muted">Protocol: {encryptionStatus.inTransitProtocol || 'Not specified'}</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Alert variant="warning" className="mt-3">
              <strong>CCPA Best Practice:</strong> While not explicitly required, encryption is considered a 
              reasonable security practice for protecting consumer personal information.
            </Alert>
            </div>
          )}
          

         <div className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "2" ? null : "2")
    }
  >
    <h6>Access Controls & Consumer Data Protection</h6>
    <span>{activeAccordionKey === "2" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "2" && (
    <div className="p-3 border-top">
            <Button variant="primary" className="mb-3" onClick={() => setShowAccessControlModal(true)}>
          <FaPlus /> Add Access Control
           </Button>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Control Type</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {accessControls.map((control, index) => (
                  <tr key={index}>
                    <td>{control.type}</td>
                    <td>{control.description}</td>
                    <td>
                      <Badge bg={control.status === 'Active' ? 'success' : 'warning'}>
                        {control.status}
                      </Badge>
                    </td>
                    <td>
                      <ProgressBar 
                        now={control.coverage} 
                        label={`${control.coverage}%`}
                        variant={control.coverage >= 80 ? 'success' : control.coverage >= 50 ? 'warning' : 'danger'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
  )}
      </div>

      <Modal show={showModal} onHide={resetForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Security Measure</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={newMeasure.category}
                    onChange={(e) => setNewMeasure({...newMeasure, category: e.target.value})}
                  >
                    <option value="">Select Category</option>
                    {SECURITY_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Implementation Status</Form.Label>
                  <Form.Select
                    value={newMeasure.implementation}
                    onChange={(e) => setNewMeasure({...newMeasure, implementation: e.target.value})}
                  >
                    <option value="Implemented">Implemented</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Not Implemented">Not Implemented</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Security Measure</Form.Label>
              <Form.Control
                type="text"
                value={newMeasure.measure}
                onChange={(e) => setNewMeasure({...newMeasure, measure: e.target.value})}
                placeholder="e.g., Two-factor authentication for consumer data access"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newMeasure.description}
                onChange={(e) => setNewMeasure({...newMeasure, description: e.target.value})}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Responsible Party</Form.Label>
                  <Form.Control
                    type="text"
                    value={newMeasure.responsibleParty}
                    onChange={(e) => setNewMeasure({...newMeasure, responsibleParty: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Review Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newMeasure.reviewDate}
                    onChange={(e) => setNewMeasure({...newMeasure, reviewDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>CCPA Relevance</Form.Label>
              <Form.Select
                value={newMeasure.ccpaRelevance}
                onChange={(e) => setNewMeasure({...newMeasure, ccpaRelevance: e.target.value})}
              >
                <option value="">Select Relevance</option>
                {CCPA_RELEVANCE.map(relevance => (
                  <option key={relevance} value={relevance}>{relevance}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveMeasure}>Save Measure</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showAccessControlModal} onHide={resetAccessControlForm} size="lg">
  <Modal.Header closeButton>
    <Modal.Title>Add Access Control</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <Form>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Control Type</Form.Label>
            <Form.Select
              value={newAccessControl.type}
              onChange={(e) => setNewAccessControl({...newAccessControl, type: e.target.value})}
            >
              <option value="">Select Control Type</option>
              {ACCESS_CONTROL_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={newAccessControl.status}
              onChange={(e) => setNewAccessControl({...newAccessControl, status: e.target.value})}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Pending">Pending</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={newAccessControl.description}
          onChange={(e) => setNewAccessControl({...newAccessControl, description: e.target.value})}
          placeholder="Describe the access control mechanism and its CCPA relevance"
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Coverage Percentage</Form.Label>
        <Form.Range
          value={newAccessControl.coverage}
          onChange={(e) => setNewAccessControl({...newAccessControl, coverage: parseInt(e.target.value)})}
          min={0}
          max={100}
        />
        <Form.Text className="text-muted">Current: {newAccessControl.coverage}%</Form.Text>
      </Form.Group>
    </Form>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={resetAccessControlForm}>Cancel</Button>
    <Button variant="primary" onClick={handleSaveAccessControl}>Save Access Control</Button>
  </Modal.Footer>
</Modal>

    </>
  );
};

export default CCPASecurityEncryptionTab;

